/*
 * Rutas de la página del proyecto (plan
 * docs/superpowers/plans/2026-10-13-etapas-y-versiones.md §8). Gestión de
 * Proyectos vive en cuatro prefijos (ODP, LIDER, AUDITORIA y la raíz), y la
 * página, los editores y el alta de riesgo cuelgan del mismo prefijo que la
 * lista desde la que se llegó:
 *
 *   /ODP/VISUALIZARPROY                         la lista
 *   /ODP/VISUALIZARPROY/proyecto/:id            la página (?pestana=…&etapa=…)
 *   /ODP/VISUALIZARPROY/proyecto/:id/version/:v el detalle de una versión (A2)
 *   /ODP/VISUALIZARPROY/proyecto/edicion/:id    editor de proyecto
 *   /ODP/VISUALIZARPROY/etapa/alta/:id          alta de etapa
 *   /ODP/VISUALIZARPROY/etapa/edicion/:id/:et   editor de etapa
 *   /ODP/riesgos/alta/:id                       alta de riesgo (también /LIDER/…,
 *                                               /AUDITORIA/… y /riesgos/alta/:id)
 *
 * Funciones puras: reciben la URL actual del router.
 */

/** Las pestañas de la página, en su orden. */
export type PestanaProyecto = "resumen" | "etapas" | "seguimientos" | "riesgos";

export const PESTANAS_PROYECTO: readonly PestanaProyecto[] = ["resumen", "etapas", "seguimientos", "riesgos"];

/** Prefijos con hijos propios en app.routing.ts; cualquier otro se trata como la raíz. */
const PREFIJOS_CON_HIJOS: ReadonlySet<string> = new Set(["ODP", "LIDER", "AUDITORIA"]);
const RAIZ = "VISUALIZARPROY";

/** La pestaña del query param; cualquier otra cosa (o nada) es Resumen. */
export function pestanaDeTexto(texto: string | null | undefined): PestanaProyecto {
  const buscada = (texto ?? "").trim().toLowerCase();
  return PESTANAS_PROYECTO.find((p) => p === buscada) ?? "resumen";
}

/**
 * El prefijo del flujo, como lo calculan la lista y los editores (también la
 * llave de la copia del proyecto en sessionStorage): ODP, LIDER, AUDITORIA o
 * VISUALIZARPROY (la raíz). Sin ninguno de ellos, ODP.
 */
export function prefijoDeFlujo(url: string | null | undefined): string {
  const primero = segmentos(url)[0] ?? "";
  if (PREFIJOS_CON_HIJOS.has(primero) || primero === RAIZ) {
    return primero;
  }
  return "ODP";
}

/** La lista de Gestión de Proyectos del flujo: /VISUALIZARPROY o /ODP/VISUALIZARPROY. */
export function rutaListaProyectos(url: string | null | undefined): string {
  const prefijo = prefijoDeFlujo(url);
  return prefijo === RAIZ ? `/${RAIZ}` : `/${prefijo}/${RAIZ}`;
}

/** La página del proyecto en el flujo de la URL actual. */
export function rutaPaginaProyecto(url: string | null | undefined, idProyecto: string | number): string {
  return `${rutaListaProyectos(url)}/proyecto/${encodeURIComponent(String(idProyecto))}`;
}

/** El detalle de una versión (A2) en el flujo de la URL actual. */
export function rutaDetalleVersion(url: string | null | undefined, idProyecto: string | number, idVersion: string): string {
  return `${rutaPaginaProyecto(url, idProyecto)}/version/${encodeURIComponent(idVersion)}`;
}

/** El editor de proyecto (el mismo que abre la tarjeta). */
export function rutaEditorProyecto(url: string | null | undefined, idProyecto: string | number): string {
  return `${rutaListaProyectos(url)}/proyecto/edicion/${encodeURIComponent(String(idProyecto))}`;
}

/** El alta de etapa o, con el id de la etapa en la ruta, su edición. */
export function rutaEditorEtapa(url: string | null | undefined, idProyecto: string | number, idEtapaRuta: string | null): string {
  const base = rutaListaProyectos(url);
  const proyecto = encodeURIComponent(String(idProyecto));
  return idEtapaRuta
    ? `${base}/etapa/edicion/${proyecto}/${encodeURIComponent(idEtapaRuta)}`
    : `${base}/etapa/alta/${proyecto}`;
}

/** El alta de riesgo: /ODP/riesgos/alta/:id, /LIDER/…, /AUDITORIA/… o /riesgos/alta/:id (como la tarjeta). */
export function rutaAltaRiesgo(url: string | null | undefined, idProyecto: string | number): string[] {
  const primero = segmentos(url)[0];
  const base = PREFIJOS_CON_HIJOS.has(primero) ? ["/", primero] : ["/"];
  return [...base, "riesgos", "alta", String(idProyecto)];
}

/** La llave de la copia del proyecto que leen los editores si se recarga la página (sessionStorage). */
export function llaveCopiaProyecto(url: string | null | undefined, idProyecto: string | number): string {
  return `adm-proyectos:${prefijoDeFlujo(url)}:project:${idProyecto}`;
}

/**
 * La URL de regreso que manda la página a un editor (state.returnUrl): solo
 * una ruta de esta aplicación («/…», nunca «//…» ni otro origen). null si no
 * hay o no sirve, y el editor regresa a la lista como siempre.
 */
export function rutaDeRegreso(valor: unknown): string | null {
  if (typeof valor !== "string") {
    return null;
  }
  const ruta = valor.trim();
  return ruta.startsWith("/") && !ruta.startsWith("//") && !ruta.includes("\\") ? ruta : null;
}

/** Lo que se lee de una etapa del proyecto para encontrarla y nombrarla en la ruta del editor. */
export interface EtapaDeRuta {
  id?: string | null;
  idEtapa?: string | number | null;
  nombre?: string | null;
}

/**
 * La etapa del proyecto que se va a editar desde A1: por su _id y, solo si
 * la etapa se guardó sin él, por nombre. null si ya no está (la copia del
 * proyecto es anterior).
 */
export function etapaDelProyecto<T extends EtapaDeRuta>(
  etapas: readonly T[] | null | undefined,
  idEtapa: string | null | undefined,
  nombre: string | null | undefined,
): T | null {
  const lista = etapas ?? [];
  if (idEtapa) {
    return lista.find((etapa) => etapa?.id === idEtapa) ?? null;
  }
  return lista.find((etapa) => !etapa?.id && etapa?.nombre === nombre) ?? null;
}

/** El id de la etapa en la ruta del editor, como lo arma la tarjeta: idEtapa, id o nombre. */
export function idEtapaDeRuta(etapa: EtapaDeRuta | null | undefined): string | null {
  if (!etapa) {
    return null;
  }
  return String(etapa.idEtapa ?? etapa.id ?? etapa.nombre ?? "").trim() || null;
}

function segmentos(url: string | null | undefined): string[] {
  const ruta = (url ?? "").split(/[?#]/)[0];
  return ruta.split("/").filter(Boolean);
}
